// Copyright (C) 2025, Lifefinder Systems International AB, all rights reserved.
import React, { useState } from 'react';

import { isV2ApiEnabled } from '../../../api';
import type { LfDevice, DeviceViewProps, LfDeviceStatus } from '../../../types';
import { lfDeviceAPI } from '../../../utils/apiClient';
import { formatRelativeTime } from '../../../utils/dateUtils';
import { useWarnings } from '../../../utils/hooks';
import { WARNING_TYPE_LABELS } from '../../../utils/maintenanceUtils';
import { Button } from '../../common/Button';

import { ReplaceDeviceModal } from './ReplaceDeviceModal';

const getTempColor = (temp: number | null | undefined): string => {
  if (temp == null) return '#999';
  if (temp > 50 || temp < -5) return '#f44336';
  if (temp > 35 || temp < 10) return '#ff9800';
  return '#4caf50';
};

const getHumidityColor = (hum: number | null | undefined): string => {
  if (hum == null) return '#999';
  if (hum > 85) return '#f44336';
  if (hum > 70 || hum < 20) return '#ff9800';
  return '#4caf50';
};

const fmt1 = (v: number | null | undefined): string => (v != null ? v.toFixed(1) : '-');

/**
 * DeviceView Component - LF Device management
 *
 * Displays LF Devices (LifeFinder Devices) with:
 * - Device information (name, description, last seen)
 * - Device status (active, reserve, retired, maintenance)
 * - Warning indicators from maintenance system
 * - Replace functionality for active devices
 * - Activate functionality for inactive devices
 * - Filter to show/hide inactive devices
 * - Status change with optional description update
 *
 * Status color coding:
 * - Active: Blue (#2196F3)
 * - Reserve: Purple (#9C27B0)
 * - Retired: Gray (#757575)
 * - Maintenance: Orange/Red (#FF5722)
 *
 * @example
 * <DeviceView
 *   devices={devices}
 *   onRefresh={fetchDevices}
 * />
 */
export const DeviceView: React.FC<DeviceViewProps> = ({ devices, onRefresh }) => {
  const [selectedDevice, setSelectedDevice] = useState<LfDevice | null>(null);
  const [replaceMode, setReplaceMode] = useState<boolean>(false);
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [activateMode, setActivateMode] = useState<boolean>(false);
  const [newDescription, setNewDescription] = useState<string>('');
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editStatus, setEditStatus] = useState<LfDeviceStatus>('active');
  const [editDescription, setEditDescription] = useState<string>('');

  // Use shared warnings hook
  const { getWarningsForDevice } = useWarnings({ category: 'lf_device' });

  /**
   * Handle device replacement completion
   */
  const handleDeviceReplacement = (): void => {
    setReplaceMode(false);
    setSelectedDevice(null);
    // Refresh data to show updated devices
    if (onRefresh) onRefresh();
  };

  /**
   * Handle device activation
   * Changes device status to 'active' and optionally updates description
   * Supports both legacy (GET with query params) and v2 (POST with JSON body) APIs
   */
  const handleActivateDevice = async (): Promise<void> => {
    if (!selectedDevice) return;

    try {
      const descriptionParam = newDescription || selectedDevice.description;
      const useV2 = isV2ApiEnabled();

      const data = useV2
        ? await lfDeviceAPI.updateStatus(selectedDevice.deveui, 'active', descriptionParam)
        : await lfDeviceAPI.updateStatusLegacy(selectedDevice.deveui, 'active', descriptionParam);

      if (data.success) {
        setActivateMode(false);
        setSelectedDevice(null);
        setNewDescription('');
        // Refresh the device list
        if (onRefresh) onRefresh();
      } else {
        alert(`Failed to activate device: ${data.error}`);
      }
    } catch (err) {
      console.error('Error activating device:', err);
      alert(`Failed to activate device: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  /**
   * Handle device edit (status and/or description change)
   * Supports both legacy (GET with query params) and v2 (POST with JSON body) APIs
   */
  const handleEditDevice = async (): Promise<void> => {
    if (!selectedDevice) return;

    try {
      const useV2 = isV2ApiEnabled();

      const data = useV2
        ? await lfDeviceAPI.updateStatus(selectedDevice.deveui, editStatus, editDescription)
        : await lfDeviceAPI.updateStatusLegacy(selectedDevice.deveui, editStatus, editDescription);

      if (data.success) {
        setEditMode(false);
        setSelectedDevice(null);
        setEditStatus('active');
        setEditDescription('');
        // Refresh the device list
        if (onRefresh) onRefresh();
      } else {
        alert(`Failed to update device: ${data.error}`);
      }
    } catch (err) {
      console.error('Error updating device:', err);
      alert(`Failed to update device: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Filter devices based on status
  const filteredDevices = showInactive
    ? devices
    : devices.filter((d) => d.status === 'active' || !d.status);

  const activeCount = devices.filter((d) => d.status === 'active' || !d.status).length;
  const inactiveCount = devices.filter((d) => d.status && d.status !== 'active').length;

  const statusColors: Record<LfDeviceStatus, string> = {
    active: '#2196F3',
    reserve: '#9C27B0',
    retired: '#757575',
    maintenance: '#FF5722',
    deactive: '#9E9E9E',
  };

  return (
    <div>
      {/* Overall statistics and filter */}
      <div
        style={{
          background: '#f9f9f9',
          padding: '1.5vmin',
          borderRadius: '0.5vmin',
          marginBottom: '2vmin',
          display: 'flex',
          gap: '2vmin',
          fontSize: '1.8vmin',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', gap: '2vmin', flexWrap: 'wrap' }}>
          <div>
            <strong>📊 Total:</strong> {devices.length} LifeFinder Devices
          </div>
          <div>
            <strong>✓ Active:</strong> {activeCount}
          </div>
          <div>
            <strong>⏸ Inactive:</strong> {inactiveCount}
          </div>
        </div>
        <Button
          text={showInactive ? '👁 Hide Inactive' : '👁 Show Inactive'}
          onClick={() => setShowInactive(!showInactive)}
          style={{
            background: showInactive ? '#FF9800' : '#4CAF50',
            color: 'white',
            fontSize: '1.6vmin',
            padding: '0.8vmin 1.5vmin',
            border: 'none',
            borderRadius: '0.5vmin',
          }}
        />
      </div>

      {/* Devices list */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '2vmin',
        }}
      >
        {filteredDevices.length === 0 ? (
          <div
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '4vmin',
              fontSize: '2vmin',
              color: '#666',
            }}
          >
            No LifeFinder Devices found
          </div>
        ) : (
          filteredDevices.map((device) => {
            const deviceWarnings = getWarningsForDevice(device.deveui);
            const hasWarnings = deviceWarnings.length > 0;
            const hasCritical = deviceWarnings.some((w) => w.severity === 'critical');
            const isInactive = device.status && device.status !== 'active';
            const borderColor = hasCritical
              ? '#f44'
              : hasWarnings
                ? '#ff9800'
                : isInactive
                  ? statusColors[device.status]
                  : '#2196F3';

            return (
              <div
                key={device.deveui}
                onClick={() => setSelectedDevice(device)}
                style={{
                  background: isInactive ? '#f5f5f5' : 'white',
                  borderRadius: '1vmin',
                  padding: '1.5vmin',
                  border: `2px solid ${borderColor}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.1s',
                  position: 'relative',
                  opacity: isInactive ? 0.7 : 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                {/* Warning indicator */}
                {hasWarnings && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '1vmin',
                      right: '1vmin',
                      fontSize: '2.5vmin',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5vmin',
                    }}
                  >
                    <span>{hasCritical ? '🔴' : '⚠️'}</span>
                    <span
                      style={{
                        fontSize: '1.4vmin',
                        background: hasCritical ? '#f44' : '#ff9800',
                        color: 'white',
                        borderRadius: '50%',
                        width: '2.5vmin',
                        height: '2.5vmin',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {deviceWarnings.length}
                    </span>
                  </div>
                )}

                <div
                  style={{
                    fontSize: '2vmin',
                    fontWeight: 'bold',
                    marginBottom: '1vmin',
                    paddingRight: hasWarnings ? '6vmin' : '0',
                  }}
                >
                  {device.description || device.deveui}
                </div>

                <div style={{ fontSize: '1.6vmin', color: '#666', marginBottom: '0.5vmin' }}>
                  <strong>Name:</strong> {device.name || 'N/A'}
                </div>

                {/* Status badge */}
                {isInactive && (
                  <div
                    style={{
                      fontSize: '1.4vmin',
                      display: 'inline-block',
                      padding: '0.3vmin 0.8vmin',
                      borderRadius: '0.3vmin',
                      background: statusColors[device.status],
                      color: 'white',
                      marginBottom: '0.5vmin',
                      fontWeight: 'bold',
                    }}
                  >
                    {device.status.toUpperCase()}
                  </div>
                )}

                <div style={{ fontSize: '1.4vmin', color: '#888', marginTop: '1vmin' }}>
                  <div>Last seen: {formatRelativeTime(device.last_seen)}</div>
                  {(device.temperature != null || device.humidity != null) && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '1vmin',
                        marginTop: '0.5vmin',
                        justifyContent: 'center',
                      }}
                    >
                      {device.temperature != null && (
                        <span style={{ color: getTempColor(device.temperature) }}>
                          🌡️ {fmt1(device.temperature)}°C
                        </span>
                      )}
                      {device.humidity != null && (
                        <span style={{ color: getHumidityColor(device.humidity) }}>
                          💧 {fmt1(device.humidity)}%
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Device detail popup */}
      {selectedDevice && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 3000,
          }}
          onClick={() => {
            setSelectedDevice(null);
            setActivateMode(false);
            setNewDescription('');
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '1vmin',
              padding: '2vmin',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2vmin',
                borderBottom: '2px solid #ddd',
                paddingBottom: '1vmin',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '2.5vmin' }}>
                {selectedDevice.description || selectedDevice.deveui}
              </h2>
              <Button
                text="✕"
                onClick={() => {
                  setSelectedDevice(null);
                  setActivateMode(false);
                  setEditMode(false);
                  setNewDescription('');
                  setEditStatus('active');
                  setEditDescription('');
                }}
                style={{
                  background: '#f44',
                  color: 'white',
                  fontSize: '2vmin',
                  padding: '0.5vmin 1vmin',
                }}
              />
            </div>

            {/* Details */}
            <div style={{ fontSize: '1.8vmin', lineHeight: '2.5' }}>
              <div>
                <strong>Name:</strong> {selectedDevice.name || 'N/A'}
              </div>
              <div>
                <strong>Description:</strong> {selectedDevice.description || 'N/A'}
              </div>
              <div>
                <strong>Status:</strong>{' '}
                <span
                  style={{
                    color: selectedDevice.status === 'active' ? '#4CAF50' : '#FF9800',
                    fontWeight: 'bold',
                  }}
                >
                  {selectedDevice.status ? selectedDevice.status.toUpperCase() : 'ACTIVE'}
                </span>
              </div>
              <div>
                <strong>Last seen:</strong> {formatRelativeTime(selectedDevice.last_seen)}
              </div>
              {(selectedDevice.temperature != null ||
                selectedDevice.humidity != null ||
                selectedDevice.rssi != null) && (
                <div
                  style={{
                    display: 'flex',
                    gap: '2vmin',
                    marginTop: '0.5vmin',
                    fontSize: '1.8vmin',
                    justifyContent: 'center',
                  }}
                >
                  {selectedDevice.temperature != null && (
                    <span style={{ color: getTempColor(selectedDevice.temperature) }}>
                      🌡️ {fmt1(selectedDevice.temperature)}°C
                    </span>
                  )}
                  {selectedDevice.humidity != null && (
                    <span style={{ color: getHumidityColor(selectedDevice.humidity) }}>
                      💧 {fmt1(selectedDevice.humidity)}%
                    </span>
                  )}
                  {selectedDevice.rssi != null && <span>📡 {selectedDevice.rssi} dBm</span>}
                </div>
              )}
            </div>

            {/* Warnings */}
            {(() => {
              const deviceWarnings = getWarningsForDevice(selectedDevice.deveui);
              if (deviceWarnings.length > 0) {
                return (
                  <div
                    style={{
                      marginTop: '2vmin',
                      padding: '1.5vmin',
                      background: '#fff3e0',
                      borderRadius: '0.5vmin',
                      border: '2px solid #ff9800',
                    }}
                  >
                    <div style={{ fontSize: '1.8vmin', fontWeight: 'bold', marginBottom: '1vmin' }}>
                      ⚠️ Active Warnings ({deviceWarnings.length})
                    </div>
                    {deviceWarnings.map((warning, idx) => {
                      return (
                        <div
                          key={idx}
                          style={{
                            fontSize: '1.5vmin',
                            padding: '0.8vmin',
                            marginBottom: '0.5vmin',
                            background: warning.severity === 'critical' ? '#ffebee' : '#fff',
                            borderLeft: `3px solid ${warning.severity === 'critical' ? '#f44' : '#ff9800'}`,
                            borderRadius: '0.3vmin',
                          }}
                        >
                          <div style={{ fontWeight: 'bold' }}>
                            {WARNING_TYPE_LABELS[warning.warning_type] || warning.warning_type}
                          </div>
                          <div style={{ fontSize: '1.3vmin', color: '#666' }}>
                            Detected: {new Date(warning.detected_at).toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return null;
            })()}

            {/* Activate device form */}
            {selectedDevice.status === 'deactive' && activateMode && (
              <div
                style={{
                  marginTop: '2vmin',
                  padding: '1.5vmin',
                  background: '#e8f5e9',
                  borderRadius: '0.5vmin',
                  border: '2px solid #4CAF50',
                }}
              >
                <div style={{ fontSize: '1.8vmin', fontWeight: 'bold', marginBottom: '1vmin' }}>
                  Activate Device
                </div>
                <div style={{ fontSize: '1.4vmin', marginBottom: '1vmin' }}>
                  <label style={{ display: 'block', marginBottom: '0.5vmin' }}>
                    <strong>Description (optional):</strong>
                  </label>
                  <input
                    type="text"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder={selectedDevice.description || 'Enter description...'}
                    style={{
                      width: '100%',
                      fontSize: '1.6vmin',
                      padding: '0.8vmin',
                      border: '1px solid #ccc',
                      borderRadius: '0.3vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1vmin' }}>
                  <Button
                    text="✓ Activate"
                    onClick={handleActivateDevice}
                    style={{
                      flex: 1,
                      background: '#4CAF50',
                      color: 'white',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                    }}
                  />
                  <Button
                    text="Cancel"
                    onClick={() => {
                      setActivateMode(false);
                      setNewDescription('');
                    }}
                    style={{
                      flex: 1,
                      background: '#ccc',
                      color: '#333',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Edit device form */}
            {editMode && (
              <div
                style={{
                  marginTop: '2vmin',
                  padding: '1.5vmin',
                  background: '#e3f2fd',
                  borderRadius: '0.5vmin',
                  border: '2px solid #2196F3',
                }}
              >
                <div style={{ fontSize: '1.8vmin', fontWeight: 'bold', marginBottom: '1vmin' }}>
                  Edit Device
                </div>
                <div style={{ fontSize: '1.4vmin', marginBottom: '1vmin' }}>
                  <label style={{ display: 'block', marginBottom: '0.5vmin' }}>
                    <strong>Status:</strong>
                  </label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value as LfDeviceStatus)}
                    style={{
                      width: '100%',
                      fontSize: '1.6vmin',
                      padding: '0.8vmin',
                      border: '1px solid #ccc',
                      borderRadius: '0.3vmin',
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="reserve">Reserve</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retired</option>
                    <option value="deactive">Deactive</option>
                  </select>
                </div>
                <div style={{ fontSize: '1.4vmin', marginBottom: '1vmin' }}>
                  <label style={{ display: 'block', marginBottom: '0.5vmin' }}>
                    <strong>Description:</strong>
                  </label>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Enter description..."
                    style={{
                      width: '100%',
                      fontSize: '1.6vmin',
                      padding: '0.8vmin',
                      border: '1px solid #ccc',
                      borderRadius: '0.3vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1vmin' }}>
                  <Button
                    text="✓ Save Changes"
                    onClick={handleEditDevice}
                    style={{
                      flex: 1,
                      background: '#2196F3',
                      color: 'white',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                    }}
                  />
                  <Button
                    text="Cancel"
                    onClick={() => {
                      setEditMode(false);
                      setEditStatus('active');
                      setEditDescription('');
                    }}
                    style={{
                      flex: 1,
                      background: '#ccc',
                      color: '#333',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Control buttons */}
            <div
              style={{
                marginTop: '2vmin',
                paddingTop: '2vmin',
                borderTop: '1px solid #ddd',
                display: 'flex',
                gap: '1vmin',
                flexWrap: 'wrap',
              }}
            >
              {!editMode && !activateMode && (
                <Button
                  text="✏️ Edit Device"
                  onClick={() => {
                    setEditMode(true);
                    setEditStatus(selectedDevice.status || 'active');
                    setEditDescription(selectedDevice.description || '');
                  }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: '#2196F3',
                    color: 'white',
                    fontSize: '1.6vmin',
                    padding: '1vmin 1.5vmin',
                    border: 'none',
                    borderRadius: '0.5vmin',
                    boxSizing: 'border-box',
                  }}
                />
              )}
              {selectedDevice.status === 'deactive' && !activateMode && !editMode && (
                <Button
                  text="✓ Activate Device"
                  onClick={() => setActivateMode(true)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: '#4CAF50',
                    color: 'white',
                    fontSize: '1.6vmin',
                    padding: '1vmin 1.5vmin',
                    border: 'none',
                    borderRadius: '0.5vmin',
                    boxSizing: 'border-box',
                  }}
                />
              )}
              {(selectedDevice.status === 'active' || !selectedDevice.status) && !editMode && (
                <Button
                  text="🔄 Replace"
                  onClick={() => setReplaceMode(true)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: '#FF9800',
                    color: 'white',
                    fontSize: '1.6vmin',
                    padding: '1vmin 1.5vmin',
                    border: 'none',
                    borderRadius: '0.5vmin',
                    boxSizing: 'border-box',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Replace device modal */}
      {replaceMode && selectedDevice && (
        <ReplaceDeviceModal
          device={selectedDevice}
          deviceCategory="lf_device"
          onReplace={handleDeviceReplacement}
          onCancel={() => setReplaceMode(false)}
        />
      )}
    </div>
  );
};

export default DeviceView;
